import { deserializeGameState } from '@shared/protocol';
import type { ServerMessage } from '@shared/protocol';
import type { GameState } from '@shared/game/types';
import type { BoardLiveHudState, BoardMountPayload, BoardTimerContext } from './board-payload';

export interface ActiveBoardTimer {
  readonly context: BoardTimerContext;
  readonly deadlineAtMs: number;
}

export interface BoardTopTimerHandle {
  readonly element: HTMLElement;
  updateFromGameState(state: GameState): void;
  updateFromLiveHudState(state: BoardLiveHudState): void;
  dispose(): void;
}

export interface BoardTopTimerOptions {
  readonly subscribe?: boolean;
}

const CONTEXT_LABELS: Record<BoardTimerContext, string> = {
  using_item: 'Using item',
  roll: 'Roll',
  choice: 'Choice',
  fork_choice: 'Fork choice',
};

function validDeadline(deadlineAtMs: number | null | undefined): number | null {
  return typeof deadlineAtMs === 'number' && Number.isFinite(deadlineAtMs) ? deadlineAtMs : null;
}

export function deriveActiveBoardTimer(state: GameState): ActiveBoardTimer | null {
  const pendingEncounterDeadline = validDeadline(state.pendingBoardEncounter?.deadlineAtMs);
  if (pendingEncounterDeadline != null) {
    return { context: 'choice', deadlineAtMs: pendingEncounterDeadline };
  }

  const pendingSoulAnchorDeadline = validDeadline(state.pendingSoulAnchorClaim?.deadlineAtMs);
  if (pendingSoulAnchorDeadline != null) {
    return { context: 'choice', deadlineAtMs: pendingSoulAnchorDeadline };
  }

  if (state.phase === 'fork_choice') {
    const deadline =
      validDeadline(state.forkChoiceDeadlineAtMs) ??
      validDeadline(state.pendingForkChoice?.deadlineAtMs);
    return deadline == null ? null : { context: 'fork_choice', deadlineAtMs: deadline };
  }

  if (state.phase === 'item_phase') {
    const deadline = validDeadline(state.turnActionDeadlineAtMs);
    return deadline == null ? null : { context: 'using_item', deadlineAtMs: deadline };
  }

  if (state.phase === 'rolling') {
    const deadline = validDeadline(state.turnActionDeadlineAtMs);
    return deadline == null ? null : { context: 'roll', deadlineAtMs: deadline };
  }

  return null;
}

export function deriveActiveBoardTimerFromLiveHudState(
  state: BoardLiveHudState,
): ActiveBoardTimer | null {
  const context = state.activeTimerContext ?? null;
  const deadline = validDeadline(state.activeTimerDeadlineAtMs);
  if (context != null && deadline != null) return { context, deadlineAtMs: deadline };

  if (state.phase === 'fork_choice') {
    const forkDeadline = validDeadline(state.forkChoiceDeadlineAtMs);
    return forkDeadline == null ? null : { context: 'fork_choice', deadlineAtMs: forkDeadline };
  }
  if (state.phase === 'item_phase') {
    const turnDeadline = validDeadline(state.turnActionDeadlineAtMs);
    return turnDeadline == null ? null : { context: 'using_item', deadlineAtMs: turnDeadline };
  }
  if (state.phase === 'rolling') {
    const turnDeadline = validDeadline(state.turnActionDeadlineAtMs);
    return turnDeadline == null ? null : { context: 'roll', deadlineAtMs: turnDeadline };
  }
  return null;
}

function formatRemainingClock(deadlineAtMs: number, now = Date.now()): string {
  const remainingSeconds = Math.max(0, Math.ceil((deadlineAtMs - now) / 1_000));
  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = String(remainingSeconds % 60).padStart(2, '0');
  return `${minutes}:${seconds}s`;
}

function renderTimerText(timer: ActiveBoardTimer): string {
  return `${CONTEXT_LABELS[timer.context]} ${formatRemainingClock(timer.deadlineAtMs)}`;
}

function applyTopTimerStyle(element: HTMLElement): void {
  element.style.cssText = [
    'position: fixed',
    'top: max(12px, env(safe-area-inset-top))',
    'left: 50%',
    'transform: translateX(-50%)',
    'z-index: 10100',
    'max-width: calc(100vw - 32px)',
    'box-sizing: border-box',
    'padding: 8px 18px',
    'border: 2px solid rgba(255,255,255,0.18)',
    'border-radius: 9999px',
    'background: rgba(30,35,50,0.90)',
    'box-shadow: 0 6px 18px rgba(0,0,0,0.36)',
    "font-family: var(--typography-family-body, 'Nunito Sans', 'Nunito', 'Arial Rounded MT Bold', sans-serif)",
    'font-size: var(--typography-size-timer, 48px)',
    'font-weight: 900',
    'font-variant-numeric: tabular-nums',
    'line-height: 1',
    'letter-spacing: 0',
    'text-align: center',
    'overflow-wrap: anywhere',
    'color: var(--color-text-primary, #FFFFFF)',
    'text-shadow: var(--typography-shadow-hud, 0 2px 0 #000000, 0 3px 6px rgba(0,0,0,0.50))',
    'pointer-events: none',
    'user-select: none',
    'display: none',
  ].join('; ');
}

export function createBoardTopTimer(
  payload: BoardMountPayload,
  options: BoardTopTimerOptions = {},
): BoardTopTimerHandle {
  let disposed = false;
  let currentTimer: ActiveBoardTimer | null = deriveActiveBoardTimer(payload.state);
  let intervalId: number | null = null;

  const element = document.createElement('div');
  element.className = 'board-top-timer';
  element.setAttribute('data-board-top-timer', '');
  applyTopTimerStyle(element);

  function stopInterval(): void {
    if (intervalId == null) return;
    window.clearInterval(intervalId);
    intervalId = null;
  }

  function ensureInterval(): void {
    if (intervalId != null || currentTimer == null) return;
    intervalId = window.setInterval(render, 250);
  }

  function render(): void {
    if (disposed) return;
    if (currentTimer == null) {
      element.textContent = '';
      element.style.display = 'none';
      element.removeAttribute('data-tutorial-target');
      stopInterval();
      return;
    }
    element.textContent = renderTimerText(currentTimer);
    element.setAttribute('data-tutorial-target', 'round-timer');
    element.style.display = 'block';
    ensureInterval();
  }

  function setTimer(timer: ActiveBoardTimer | null): void {
    currentTimer = timer;
    render();
  }

  const shouldSubscribe = options.subscribe !== false;
  const unsubscribeState = shouldSubscribe
    ? payload.subscribe((msg: ServerMessage) => {
        if (disposed || msg.type !== 'state_sync') return;
        setTimer(deriveActiveBoardTimer(deserializeGameState(msg.state)));
      })
    : null;
  const unsubscribeLiveHud = shouldSubscribe
    ? payload.subscribeLiveHudState?.((state) => {
        if (disposed) return;
        setTimer(deriveActiveBoardTimerFromLiveHudState(state));
      })
    : null;

  render();

  return {
    element,
    updateFromGameState(state: GameState): void {
      setTimer(deriveActiveBoardTimer(state));
    },
    updateFromLiveHudState(state: BoardLiveHudState): void {
      setTimer(deriveActiveBoardTimerFromLiveHudState(state));
    },
    dispose(): void {
      if (disposed) return;
      disposed = true;
      stopInterval();
      unsubscribeState?.();
      unsubscribeLiveHud?.();
      element.remove();
    },
  };
}
